import React from 'react'
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { statusMessageKey } from 'src/helpers/errorText'

/**
 * Root-level fallback for createBrowserRouter's errorElement.
 *
 * Catches any uncaught throw (including render-time crashes like React #31)
 * and renders a friendly, styled message instead of React Router's default
 * unstyled "Unexpected Application Error" page.
 *
 * Logs the raw error to console so it can be triaged from browser devtools
 * without source-maps. The user-visible string is always translated.
 */
const RouterErrorBoundary: React.FC = () => {
  const error = useRouteError()
  const { t } = useTranslation()

  // Always log — this is the only diagnostic surface until we add Sentry.
  console.error('[router-error-boundary]', error)

  // The status in the page's language: statusText is English ("Not Found").
  const detail = isRouteErrorResponse(error)
    ? `${error.status} · ${t(statusMessageKey(error.status))}`
    : null

  const handleReload = () => {
    window.location.reload()
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen supports-[height:100dvh]:min-h-dvh px-4 text-center text-ink">
      <div className="flex flex-col gap-4 max-w-md w-full">
        <h1 className="text-2xl font-extrabold">{t('common.errors.boundary')}</h1>
        {detail && (
          <p className="text-sm text-ink-3" aria-live="polite">
            {detail}
          </p>
        )}
        <div className="flex flex-col gap-2 mt-2">
          <button
            type="button"
            onClick={handleReload}
            className="bg-inverse text-white rounded-lg py-3 px-4 hover:opacity-85 transition-opacity"
          >
            {t('reload', 'Reload')}
          </button>
          <Link to="/" className="text-ink hover:text-brand underline">
            {t('home', 'Home')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default RouterErrorBoundary
